import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { ArrowRight, PlayCircle, Tv2 } from "lucide-react"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { PATH_MAP } from "../../configuration/PathMap"
import { ETutorialAudienceType } from "../../Enum/ETutorialAudienceType"
import { TutorialEntity } from "../../Objects/Tutorial/TutorialEntity"
import { useGetPublicTutorial } from "../../hooks/Tutorial/useGetPublicTutorial"
import { extractYoutubeId } from "../../Objects/Generics/Helps"

type PreviewCard = {
  id: number
  title: string
  category: string
  audienceLabel: string
  videoId: string
}

const TutorialShowcase: React.FC = () => {
  const [tutorials, setTutorials] = useState<TutorialEntity[]>([])

  const [isLoading, setIsLoading] = useState(true)

  const { t } = useTranslation("landpage")

  const { handleGetPublicTutorialPreview } = useGetPublicTutorial()

  useEffect(() => {
    const loadTutorials = async () => {
      setIsLoading(true)

      try {
        const list = await handleGetPublicTutorialPreview()

        setTutorials(list)
      } catch {
        setTutorials([])
      } finally {
        setIsLoading(false)
      }
    }

    loadTutorials()
  }, [handleGetPublicTutorialPreview])

  const previewCards = useMemo<PreviewCard[]>(() => {
    return tutorials
      .map((item) => {
        const videoId = extractYoutubeId(item.youtubeUrl)

        return {
          id: item.id,
          title: item.title,
          category: item.category || t("tutorial_showcase.default_category"),
          audienceLabel:
            item.audience === ETutorialAudienceType.Courier
              ? t("tutorial_showcase.audience.courier")
              : item.audience === ETutorialAudienceType.Both
                ? t("tutorial_showcase.audience.both")
                : t("tutorial_showcase.audience.restaurant"),
          videoId,
        }
      })
      .filter((item) => item.videoId)
      .slice(0, 6)
  }, [t, tutorials])

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <section id="features" className="px-4 mb:px-6 pb-8 lg:pb-10">
        <div className="mx-auto overflow-hidden rounded-2xl shadow-sm max-w-[1280px]">
          <div className="grid grid-cols-1 px-4 gap-8 py-6 md:px-8 md:py-8 lg:grid-cols-2 lg:items-center lg:px-10 lg:py-10">
            <div className="flex items-center md:items-start flex-col">
              <span className="inline-flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-[0.7rem] font-extrabold uppercase tracking-[0.2em] text-red-600">
                <Tv2 size={14} /> {t("tutorial_showcase.badge")}
              </span>

              <h2 className="mt-4 max-w-[560px] text-2xl md:text-3xl text-justify md:text-left font-black leading-tight text-slate-900 lg:text-5xl">
                {t("tutorial_showcase.title")}
              </h2>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={PATH_MAP.PUBLIC_TUTORIALS_PAGE}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-5 py-3 text-sm font-black text-white transition-transform duration-300 hover:scale-105"
                  data-interaction-description-key="interactionDescriptions:components.landPage.tutorialShowcase.link.ctaOpen"
                >
                  {t("tutorial_showcase.cta_open")} <ArrowRight size={16} />
                </Link>

                <span className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-4 py-3 text-xs font-bold text-slate-600">
                  <PlayCircle size={16} className="text-red-500" /> {t("tutorial_showcase.cta_updated")}
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-16 bg-gradient-to-l from-red-50 to-transparent lg:block" />

              {isLoading ? (
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={index}
                      className="min-w-[250px] max-w-[250px] animate-pulse rounded-lg border border-red-100 bg-white p-3 shadow-sm sm:min-w-[280px] sm:max-w-[280px]"
                    >
                      <div className="aspect-video rounded-[1.3rem] bg-orange-100" />

                      <div className="mt-3 h-4 w-24 rounded bg-red-100" />

                      <div className="mt-2 h-4 w-3/4 rounded bg-orange-100" />
                    </div>
                  ))}
                </div>
              ) : previewCards.length > 0 ? (
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-none snap-x snap-mandatory">
                  {previewCards.map((item) => (
                    <Link
                      key={item.id}
                      to={PATH_MAP.PUBLIC_TUTORIALS_PAGE}
                      className="block"
                      data-interaction-description-key="interactionDescriptions:components.landPage.tutorialShowcase.link.previewCta"
                    >
                      <article className="group min-w-[320px] max-w-[320px] snap-start rounded-xl border border-red-100 bg-white p-3 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:min-w-[380px] sm:max-w-[380px] cursor-pointer">
                        <div className="relative overflow-hidden rounded-[1.3rem] border border-orange-100 bg-orange-50">
                          <img
                            src={`https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`}
                            alt={item.title}
                            className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />

                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent" />

                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-red-600 shadow-lg">
                              <PlayCircle size={28} fill="currentColor" />
                            </div>
                          </div>

                          <span className="absolute left-3 top-3 rounded-full bg-white px-2.5 py-1 text-[0.65rem] font-black uppercase tracking-wide text-red-600 shadow-sm">
                            {item.audienceLabel}
                          </span>
                        </div>

                        <div className="px-1 pb-1 pt-3">
                          <p className="text-[0.7rem] font-black uppercase tracking-wide text-orange-500">
                            {item.category}
                          </p>

                          <p className="mt-2 line-clamp-2 text-sm font-black text-slate-900">{item.title}</p>

                          <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {t("tutorial_showcase.preview_cta")}
                          </p>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-red-100 bg-white p-6 text-center shadow-sm">
                  <p className="text-xs font-black uppercase tracking-wide text-red-600">
                    {t("tutorial_showcase.badge")}
                  </p>

                  <h3 className="mt-2 text-xl font-black text-slate-900">{t("tutorial_showcase.empty_title")}</h3>

                  <p className="mt-3 text-sm text-slate-500">{t("tutorial_showcase.empty_description")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Animate>
  )
}

export default TutorialShowcase
